import { useState, useEffect } from 'react';

import { getCountry } from '@iwIntlProvider/CountryProvider';
import { DEOnly, UKOnly } from '@iwIntlProvider/CountryWrappers';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconTimesDefault } from '@iwoca/icons';
import { Accordion, Button } from '@iwoca/orion';
import { Divider } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { OpenBankingInfo } from './OpenBankingInfo';
import {
    TRUELAYER_CANCEL,
    TRUELAYER_FAILURE,
    TRUELAYER_SUCCESS
} from './truelayer.constants';
import messages from './TrueLayer.messages';
import styles from './trueLayer.module.css';
import { TruelayerAlertBox } from './TruelayerAlertbox';
import TrueLayerLinkedAccounts from './TrueLayerLinkedAccounts';
import { hasApplicationWithProductType } from '../../../../api/lendingAPI/ApplicationSelector';
import { RootState } from '../../../../iwProvider/configureStore';
import {
    getCurrentPathname,
    getQueryParameter,
    getWindowLocation,
    isLinkingOpenBankingOnly
} from '../../../../iwRouter/LocationHelper';
import ManualUpload from '../../../applyFlexiLoanView/applyFlexiLoanFormView/NewDocUploads/RequirementTypes/BankStatements/ManualUpload';
import { REVENUE_BASED_LOAN_PRODUCT_TYPE } from '../../../multiLoanDashboard/accountsSelector';
import { getSuggestedProduct } from '../../../multiLoanDashboard/dashboardWidgets/tabularFlexiDashboard/multiLoanDashboardSelector';
import { BankSelectionModal } from '../../BankSelectionModal/BankSelectionModal';

const mapStateToProps = (state: RootState) => {
    return {
        isRBLApplication: hasApplicationWithProductType(
            state,
            REVENUE_BASED_LOAN_PRODUCT_TYPE
        ),
        suggestedAmount: getSuggestedProduct(state)?.amount
    };
};

interface Props {
    handleClose?: () => void;
    handleUploadBankStatements: () => void;
}

type PropsFromRedux = ReturnType<typeof mapStateToProps> & Props;

export type AlertStatusTypes =
    | typeof TRUELAYER_SUCCESS
    | typeof TRUELAYER_FAILURE
    | typeof TRUELAYER_CANCEL;

// TODO: write a test for trading data to open modal on truelayer url
// TODO: storybook for different states
const TrueLayer = ({
    heading,
    suggestedAmount,
    handleClose,
    handleUploadBankStatements,
    isRBLApplication
}: {
    heading?: React.ReactNode;
} & PropsFromRedux) => {
    const [bankSelectionModalOpen, setBankSelectionModalOpen] = useState(false);

    const [alertStatus, setAlertStatus] = useState<AlertStatusTypes>();

    useEffect(() => {
        const location = getWindowLocation();
        const statusCode = getQueryParameter(location, 'status_code');
        const errorDetail = getQueryParameter(location, 'error_detail');
        if (statusCode) {
            if (statusCode === '200') {
                return setAlertStatus(TRUELAYER_SUCCESS);
            }
            if (
                statusCode === '500' &&
                errorDetail &&
                errorDetail === 'access_denied'
            ) {
                return setAlertStatus(TRUELAYER_CANCEL);
            }
            if (statusCode === '500' && errorDetail !== 'access_denied') {
                return setAlertStatus(TRUELAYER_FAILURE);
            }
            return setAlertStatus(TRUELAYER_FAILURE);
        }
    }, []);

    const onClickConnectAccount = () => {
        tracking.ampli.openBankingConnectStarted({
            url: getCurrentPathname()
        });

        setBankSelectionModalOpen(true);
    };

    return (
        <>
            <BankSelectionModal
                isOpen={bankSelectionModalOpen}
                onClose={() => setBankSelectionModalOpen(false)}
            />

            <div data-testid="truelayer" className={styles.container}>
                <div className={styles.header}>
                    <h2 className={styles.heading}>
                        {heading || (
                            <FormattedMessage
                                id="_.true_layer.choose_bank_account"
                                defaultMessage="Choose which bank account to link"
                            />
                        )}
                    </h2>
                    {handleClose && (
                        <button
                            className={styles.closeModalButton}
                            onClick={() => handleClose()}
                            aria-label="close-button"
                            title="close modal button"
                        >
                            <IconTimesDefault width={34} height={34} />
                        </button>
                    )}
                </div>
                <TrueLayerLinkedAccounts />
                {alertStatus && (
                    <TruelayerAlertBox
                        handleFailureButtonClick={handleUploadBankStatements}
                        handleCancelButtonClick={handleUploadBankStatements}
                        status={alertStatus}
                    />
                )}
                <div className="mb-l flex flex-col justify-between gap-m min-sm:flex-row min-sm:items-center">
                    <OpenBankingInfo suggestedAmount={suggestedAmount} />
                    <Button
                        variant="secondary"
                        className={styles.ctaButton}
                        role="button"
                        onClick={onClickConnectAccount}
                    >
                        <FormattedMessage {...messages.connectButton} />
                    </Button>
                </div>
                <div className={styles.footer}>
                    <UKOnly>
                        {!isLinkingOpenBankingOnly() && !isRBLApplication && (
                            <FormattedMessage
                                {...messages.pdfOption}
                                values={{
                                    openModal: (
                                        <span
                                            data-testid="upload-bank-statements"
                                            className={styles.link}
                                            onClick={handleUploadBankStatements}
                                        >
                                            <FormattedMessage
                                                {...messages.uploadStatements}
                                            />
                                        </span>
                                    )
                                }}
                            />
                        )}
                    </UKOnly>

                    <DEOnly>
                        <div className="pt-xs">
                            <Divider />
                            <Accordion
                                title={
                                    <FormattedMessage
                                        {...messages.cannotLinkAccount}
                                        key="cannotLinkTitle" // Required as otherwise react moans about not having a key. This is weird behaviour when combining Accordion and FormattedMessage
                                    />
                                }
                                small
                                variant="secondary"
                            >
                                <div className={styles.manualUploadContainer}>
                                    <ManualUpload />
                                </div>
                            </Accordion>
                        </div>
                    </DEOnly>

                    {getCountry() === 'UK' && (
                        <p>
                            <FormattedMessage
                                {...messages.fcaLinkText}
                                values={{
                                    link: (
                                        <a
                                            className={styles.link}
                                            href="https://register.fca.org.uk/s/firm?id=001b00000413NAxAAM"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FormattedMessage
                                                {...messages.fcaLink}
                                            />
                                        </a>
                                    )
                                }}
                            />
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(TrueLayer);
